import React, { useState } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  UserIcon,
  AtIcon,
  LocationDotIcon,
  PhoneIcon
} from '@revfluence/fresh-icons/solid/esm';
import { Button } from '@/shadcn/components/ui/button';
import { GetMemberApplicantInfo_memberApplicantInfo_catalogOrderRequest } from '@/app/queries/types/GetMemberApplicantInfo';

interface ProductSectionProps {
  productName: string;
  cost: string;
  // Extended props for expanded view
  catalogOrderRequest?: GetMemberApplicantInfo_memberApplicantInfo_catalogOrderRequest | null;
}

export const ProductSection: React.FC<ProductSectionProps> = ({
  productName,
  cost,
  catalogOrderRequest,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleViewRequest = () => {
    if (catalogOrderRequest?.creatorOrderRequest?.line_items?.length) {
      setIsExpanded(!isExpanded);
    }
  };

  const lineItems = catalogOrderRequest?.creatorOrderRequest?.line_items || [];
  const shippingAddress = catalogOrderRequest?.creatorOrderRequest?.shipping_address;
  const memberInfo = catalogOrderRequest?.member;
  const orderNote = catalogOrderRequest?.creatorOrderRequest?.note;
  const orderEmail = catalogOrderRequest?.creatorOrderRequest?.email;

  const formatFullName = () => {
    if (!shippingAddress) return memberInfo?.name || '--';

    const fullName = [shippingAddress.first_name, shippingAddress.last_name]
      .filter(Boolean)
      .join(' ');

    return fullName || memberInfo?.name || '--';
  };

  return (
    <div className="bg-white rounded-2xl border-t-4 border-blue-4 border overflow-hidden">
      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <div className="flex-1 text-grey-6 text-base font-medium">
            Product
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-grey-6 text-xs font-medium hover:bg-grey-1 flex items-center gap-1"
            onClick={handleViewRequest}
          >
            {catalogOrderRequest?.creatorOrderRequest?.line_items?.length ? (
              <>
                {isExpanded ? 'Hide Request' : 'View Request'}
                {isExpanded ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
              </>
            ) : (
              'View Request'
            )}
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <div className="flex-1 text-grey-5 text-sm font-normal">
            Product Name
          </div>
          <div className="text-grey-6 text-sm font-normal">
            {productName}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <div className="flex-1 text-grey-5 text-sm font-normal">
            Cost
          </div>
          <div className="text-grey-6 text-sm font-normal">
            {cost}
          </div>
        </div>
      </div>

      {/* Expanded product request details */}
      {isExpanded && catalogOrderRequest?.creatorOrderRequest && (
        <div className="border-t border-grey-2 p-4">
          <div className="max-h-96 overflow-y-auto space-y-4">
            {/* Products Section */}
            {lineItems.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-3">Requested Products</h4>
                <div className="space-y-3">
                  {lineItems.map((item, index) => (
                    <div key={`${item.variant_id}-${index}`} className="flex items-center justify-between p-3 bg-grey-1 rounded-lg">
                      <div className="flex-1">
                        <div className="text-sm font-medium">{item.title}</div>
                        {item.variant_title && item.variant_title !== item.title && (
                          <div className="text-xs text-grey-5">{item.variant_title}</div>
                        )}
                        <div className="text-xs text-grey-5">Quantity: {item.quantity}</div>
                      </div>
                      <div className="text-sm font-medium">
                        {item.price > 0 ? `$${(item.price * item.quantity).toFixed(2)}` : 'Free'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Information Section */}
            <div>
              <h4 className="text-sm font-medium mb-3">Contact Information</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <UserIcon className="w-3 h-3 text-grey-5" />
                  <span className="text-sm">{formatFullName()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <AtIcon className="w-3 h-3 text-grey-5" />
                  <span className="text-sm">{orderEmail || memberInfo?.email || '--'}</span>
                </div>
                {shippingAddress?.phone && (
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="w-3 h-3 text-grey-5" />
                    <span className="text-sm">{shippingAddress.phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Address Section */}
            {shippingAddress && (
              <div>
                <h4 className="text-sm font-medium mb-3">Shipping Address</h4>
                <div className="flex items-start gap-2">
                  <LocationDotIcon className="w-3 h-3 text-grey-5 mt-0.5" />
                  <div className="text-sm">
                    <div>{formatFullName()}</div>
                    <div>{shippingAddress.address1}</div>
                    {shippingAddress.address2 && <div>{shippingAddress.address2}</div>}
                    <div>
                      {[shippingAddress.city, shippingAddress.province, shippingAddress.zip]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                    <div>{shippingAddress.country}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Order Notes Section */}
            {orderNote && (
              <div>
                <h4 className="text-sm font-medium mb-3">Order Notes</h4>
                <div className="text-sm p-3 bg-grey-1 rounded-lg">
                  {orderNote}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
